import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ImpersonationContext {
  sessionToken: string;
  actorAdminId: string;
  actorAdminEmail: string;
  targetUserId: string;
  targetEmail: string;
  targetName: string;
  expiresAt: string;
}

const STORAGE_KEY = 'imp_ctx';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly ctxSubject = new BehaviorSubject<ImpersonationContext | null>(null);

  readonly context$: Observable<ImpersonationContext | null> = this.ctxSubject.asObservable();
  readonly isImpersonating$: Observable<boolean> = this.ctxSubject.pipe(map(ctx => ctx !== null));

  private expiryTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly http: HttpClient) {
    this.restoreFromStorage();
  }

  private restoreFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw) as ImpersonationContext;
      if (new Date(ctx.expiresAt).getTime() > Date.now()) {
        this.ctxSubject.next(ctx);
        this.scheduleExpiry(ctx);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  startFromToken(token: string): Observable<ImpersonationContext> {
    return new Observable(subscriber => {
      this.http
        .post<ImpersonationContext>(`${environment.apiUrl}/admin/impersonation/validate`, { token })
        .subscribe({
          next: ctx => {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
            this.ctxSubject.next(ctx);
            this.scheduleExpiry(ctx);
            subscriber.next(ctx);
            subscriber.complete();
          },
          error: err => subscriber.error(err),
        });
    });
  }

  exit(): Observable<void> {
    const ctx = this.ctxSubject.value;
    if (!ctx) return new Observable(s => { s.complete(); });

    return new Observable(subscriber => {
      this.http
        .post<void>(`${environment.apiUrl}/admin/impersonation/exit`, { sessionToken: ctx.sessionToken })
        .subscribe({
          next: () => {
            this.clear();
            subscriber.next();
            subscriber.complete();
          },
          error: () => {
            this.clear();
            subscriber.next();
            subscriber.complete();
          },
        });
    });
  }

  getSessionToken(): string | null {
    return this.ctxSubject.value?.sessionToken ?? null;
  }

  private clear(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    sessionStorage.removeItem(STORAGE_KEY);
    this.ctxSubject.next(null);
  }

  private scheduleExpiry(ctx: ImpersonationContext): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    const msLeft = new Date(ctx.expiresAt).getTime() - Date.now();
    if (msLeft > 0) {
      this.expiryTimer = setTimeout(() => this.clear(), msLeft);
    }
  }
}
